/**
 * What a finished Baxter tool call means for the screen behind the panel.
 *
 * Rick: "anytime we are in these pages with baxter and the tool call will
 * affect the CRUD of that page we should rerender". So every successful
 * `tool_result` maps to (a) the React Query keys to invalidate and (b) the
 * DOMAIN(s) that changed, which the routes use to flash the rows that moved.
 *
 * Pure on purpose — the whole table is unit-tested in toolEffects.test.ts.
 */

import type { QueryKey } from '@tanstack/react-query';
import {
  eventQueryKeys,
  type LiveEvent,
  accountThemeKeys,
  barkKeys,
  contentItemKeys,
  contentKeys,
  contentTypeKeys,
  distributionKeys,
  formKeys,
  memberKeys,
  platformDelayKeys,
  siteKeys,
  staticFileKeys,
  tagKeys,
  themeKeys,
  tierKeys,
} from '@wordpuppi/shared';

/** A screen that can be flashed. One per route family that Baxter can touch. */
export type Domain =
  | 'pages'
  | 'posts'
  | 'content-types'
  | 'media'
  | 'theme'
  | 'forms'
  | 'bark'
  | 'members'
  | 'platforms'
  | 'settings'
  | 'site';

export const ALL_DOMAINS: Domain[] = [
  'pages',
  'posts',
  'content-types',
  'media',
  'theme',
  'forms',
  'bark',
  'members',
  'platforms',
  'settings',
  'site',
];

export interface ToolEffects {
  /** Keys to invalidate. A single `[]` means "blanket" — invalidate everything. */
  keys: QueryKey[];
  domains: Domain[];
  /** The row the call named, when it named one (an editor only reloads for its own id). */
  id?: string;
}

const NONE: ToolEffects = { keys: [], domains: [] };

/** Everything the MCP server exposes that only READS: `wpp_get_*`, `wpp_list_*`, `wpp_read_*`, `wpp_fetch_url`, `wpp_digest_url`, `wpp_status`, `wpp_login`. */
const READ_ONLY = /^wpp_(get|list|read|fetch|digest|status|login)/;

/** Tool args arrive as `unknown` off the SSE stream — never trust a shape. */
function str(args: unknown, key: string): string | undefined {
  const v = (args as Record<string, unknown> | null | undefined)?.[key];
  return typeof v === 'string' ? v : undefined;
}

/**
 * `siteSlug` is the site the admin is LOOKING at, not `args.site_slug`: the keys
 * have to match the ones the visible route mounted. A call aimed at another site
 * just costs one wasted refetch here — the alternative is a stale screen.
 * ponytail: compare the two if refetch cost ever shows up.
 */
export function effectsFor(
  call: { name: string; args?: unknown },
  siteSlug: string,
): ToolEffects {
  const { name, args } = call;
  if (READ_ONLY.test(name)) return NONE;

  /** A blueprint edit shows up in the site theme AND the account blueprint list. */
  const themeAll: QueryKey[] = [themeKeys.all(siteSlug), accountThemeKeys.all];
  // NOT `siteKeys.all` (`['sites']`): that is a PREFIX of every site-scoped key
  // in the app, so invalidating it is the blanket refetch by another name.
  const siteRow: QueryKey[] = [
    siteKeys.detail(siteSlug),
    siteKeys.settings(siteSlug),
    siteKeys.list(),
  ];

  switch (name) {
    // --- content -----------------------------------------------------------
    case 'wpp_create_content':
    case 'wpp_update_content':
    case 'wpp_delete_content': {
      const id = str(args, 'id');
      const type = str(args, 'content_type') ?? 'posts';
      if (type === 'pages')
        return { keys: [contentKeys.pages.all(siteSlug)], domains: ['pages'], id };
      if (type === 'posts')
        return {
          keys: [contentKeys.posts.all(siteSlug), tagKeys.all(siteSlug)],
          domains: ['posts'],
          id,
        };
      // A custom type's items live under their own key, and creating/deleting one
      // moves the item count shown on the content-types list.
      return {
        keys: [contentItemKeys.all(siteSlug, type), contentTypeKeys.all(siteSlug)],
        domains: ['content-types'],
        id,
      };
    }
    case 'wpp_create_content_type':
      return { keys: [contentTypeKeys.all(siteSlug)], domains: ['content-types'] };

    // --- forms -------------------------------------------------------------
    case 'wpp_create_form':
    case 'wpp_update_form':
    case 'wpp_delete_form':
      return {
        keys: [formKeys.all(siteSlug)],
        domains: ['forms'],
        id: str(args, 'form_id'),
      };

    // --- theme -------------------------------------------------------------
    case 'wpp_fork_theme':
    case 'wpp_write_theme_file':
    case 'wpp_delete_theme_file':
    case 'wpp_discard_theme':
      // `path` when the call named one, so the open file's editor only reacts to
      // ITS file; a fork/discard names none and reaches every editor, correctly.
      return { keys: themeAll, domains: ['theme'], id: str(args, 'path') };
    case 'wpp_upload_static_file':
    case 'wpp_delete_static_file':
      // Static files are the theme's own assets — the Media route lists them too.
      return {
        keys: [...themeAll, staticFileKeys.all(siteSlug)],
        domains: ['theme', 'media'],
      };

    // --- media -------------------------------------------------------------
    case 'wpp_import_media':
    case 'wpp_delete_media':
    case 'wpp_delete_media_bulk':
    case 'wpp_resize_media':
    // wpp_render_url saves its screenshot into the site's media library.
    case 'wpp_render_url':
      return {
        keys: [contentKeys.media.all(siteSlug)],
        domains: ['media'],
        id: str(args, 'id'),
      };

    // --- site + settings ---------------------------------------------------
    case 'wpp_update_settings':
      return { keys: siteRow, domains: ['settings'] };
    case 'wpp_update_site':
      return { keys: siteRow, domains: ['settings', 'site'] };
    case 'wpp_create_site':
    case 'wpp_delete_site':
      return { keys: [siteKeys.list()], domains: ['site'] };
    case 'wpp_build_site':
      // last_built_at moves, and the theme workbench's preview is built output.
      return { keys: siteRow, domains: ['site', 'theme'] };
    case 'wpp_deploy_cloudflare':
      // `['cloudflare']` is the prefix of wpp-shared's CLOUDFLARE_KEYS, which
      // that module keeps private — the prefix is the contract we can reach.
      return { keys: [...siteRow, ['cloudflare']], domains: ['site'] };
    case 'wpp_add_cloudflare_domain':
      return { keys: [...siteRow, ['cloudflare']], domains: ['site', 'settings'] };

    // --- bark / distribution ----------------------------------------------
    case 'wpp_create_bark':
    case 'wpp_add_bark_item':
    case 'wpp_schedule_bark':
    case 'wpp_cancel_bark_item':
    case 'wpp_reschedule_bark_item':
    case 'wpp_retry_bark_item':
      return { keys: [barkKeys.site(siteSlug)], domains: ['bark'] };
    case 'wpp_connect_platform':
      return {
        keys: [
          barkKeys.platformConnectionsForSite(siteSlug),
          barkKeys.availablePlatforms,
          platformDelayKeys.all(siteSlug),
        ],
        domains: ['platforms'],
      };
    case 'wpp_queue_distribution':
      return { keys: [distributionKeys.site(siteSlug), barkKeys.site(siteSlug)], domains: ['platforms', 'bark'] };

    // --- members -----------------------------------------------------------
    case 'wpp_create_member':
    case 'wpp_update_member':
    case 'wpp_delete_member':
      return {
        keys: [memberKeys.all, tierKeys.all],
        domains: ['members'],
        id: str(args, 'member_id'),
      };

    default:
      // A tool added to the MCP server later must still refresh the screen, so an
      // unknown WRITE falls back to the old blanket behaviour rather than doing
      // nothing. Delete a row from here only when the tool is genuinely read-only.
      return { keys: [[]], domains: ALL_DOMAINS };
  }
}

/** Live writes use the same site-scoped query families and existing feedback domains. */
export function effectsForEvent(event: LiveEvent, siteSlug: string): ToolEffects {
  if (event.site_slug && event.site_slug !== siteSlug) return NONE;
  return {
    keys: eventQueryKeys(event),
    domains: ALL_DOMAINS.includes(event.domain as Domain) ? [event.domain as Domain] : [],
    id: event.row_id ?? undefined,
  };
}

/** A short, plain-text label from the successful call; never fetch content for a notice. */
export function noticeFor(
  call: { name: string; args?: unknown; previousData?: unknown },
  domain: Domain,
  siteSlug: string,
): string | null {
  const effects = effectsFor(call, siteSlug);
  // An unknown write invalidates everything defensively, but cannot name a changed section.
  if (!effects.domains.includes(domain) || effects.keys.some((key) => !key.length)) return null;
  const { name, args } = call;
  const target = str(args, 'site_slug') ?? str(args, 'slug');
  if (target && target !== siteSlug) return null;

  const labels: Record<Domain, string> = {
    pages: 'Page', posts: 'Post', 'content-types': 'Custom Type', media: 'Media',
    theme: 'Theme', forms: 'Form', bark: 'Bark', members: 'Member',
    platforms: 'Platforms', settings: 'Settings', site: 'Site',
  };
  const label = domain === 'content-types' && /_content$/.test(name)
    ? 'Content Item' : labels[domain];
  const barkAction = name === 'wpp_schedule_bark' ? 'Bark scheduled' : name === 'wpp_cancel_bark_item' ? 'Bark destination cancelled' : name === 'wpp_reschedule_bark_item' ? 'Bark destination rescheduled' : name === 'wpp_retry_bark_item' ? 'Bark destination queued for retry' : null;
  const action = barkAction ?? (name.startsWith('wpp_create_') ? `New ${label} Added`
    : name.startsWith('wpp_delete_') ? `${label} Deleted`
    : /^wpp_(import|upload|render)_/.test(name) ? `${label} Added`
    : `${label} Updated`);

  // MCP accepts JSON-encoded objects for data/form_data as well as objects.
  const nested = (args as Record<string, unknown> | null | undefined)?.[
    domain === 'forms' ? 'form_data' : 'data'
  ];
  let fields: unknown = nested;
  if (typeof nested === 'string') {
    try { fields = JSON.parse(nested); } catch { fields = null; }
  }
  const title = (str(fields, 'title') ?? str(fields, 'name') ?? str(args, 'name')
    ?? (domain === 'theme' ? str(args, 'path') : undefined)
    ?? str(call.previousData, 'title') ?? str(call.previousData, 'name'))?.trim();
  const status = name === 'wpp_update_content' ? str(fields, 'status') : undefined;
  const statusAction = status === 'published' ? 'Published' : status === 'draft' ? 'Set to draft' : status === 'archived' ? 'Archived' : null;
  if (statusAction && (domain === 'posts' || domain === 'pages')) return title ? `${label}: ${title} ${statusAction}` : `${label} ${statusAction}`;
  return title ? `${action}: "${title}"` : action;
}

/** These match the Media route's actual origin and folder filters. */
export type MediaTab = 'site' | 'pages' | 'posts' | 'baxter' | 'root';
export function mediaTabsFor(rows: { origin?: string; folder?: string }[]): MediaTab[] {
  const tabs = new Set<MediaTab>();
  for (const row of rows) {
    tabs.add('root');
    if (row.origin === 'site' || row.origin === 'baxter') tabs.add(row.origin);
    if (row.folder === 'pages' || row.folder === 'posts') tabs.add(row.folder);
  }
  return [...tabs];
}

/** Only successful content writes can associate pictures; text-only edits do not. */
export function contentMediaReferences(call: { name: string; args?: unknown }, siteSlug: string): string {
  if (!['wpp_create_content', 'wpp_update_content'].includes(call.name)) return '';
  const target = str(call.args, 'site_slug');
  if (target && target !== siteSlug) return '';
  let data = (call.args as { data?: unknown } | null)?.data;
  if (typeof data === 'string') { try { data = JSON.parse(data); } catch { return ''; } }
  return data && typeof data === 'object' ? JSON.stringify(data).replace(/\\\//g, '/') : '';
}

/**
 * Did this assistant turn actually write THIS page? Gates the Revert action on
 * a canvas-selection turn (AB#810) — a turn that only talked has nothing to undo.
 */
export function touchedPage(
  calls: { name: string; status: string; args?: unknown }[] | undefined,
  pageId: string,
): boolean {
  return (calls ?? []).some((call) => {
    if (call.name !== 'wpp_update_content' || call.status !== 'ok') return false;
    const args = call.args as { content_type?: unknown; id?: unknown } | null | undefined;
    return args?.content_type === 'pages' && String(args?.id ?? '') === pageId;
  });
}
